import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext.js';
import { TenantScopeProvider } from './auth/TenantScopeContext.js';
import { SiteModeProvider } from './auth/SiteModeContext.js';
import { SignInPage } from './routes/SignInPage.js';
import { AccountSecurityPage } from './routes/AccountSecurityPage.js';
import { AdminDashboardPage } from './routes/AdminDashboardPage.js';
import { SetupPage } from './routes/SetupPage.js';
import { ForgotPasswordPage } from './routes/ForgotPasswordPage.js';
import { AcceptInvitePage } from './routes/AcceptInvitePage.js';
import { ActivityLogPage } from './routes/ActivityLogPage.js';
import { HardwareHealthPage } from './routes/HardwareHealthPage.js';
import { HardwareClaimPage } from './routes/HardwareClaimPage.js';
import { AdminAccessZonesPage } from './routes/AdminAccessZonesPage.js';
import { TenantsPage } from './routes/TenantsPage.js';
import { EmployeesPage } from './routes/EmployeesPage.js';
import { EmployeeDetailPage } from './routes/EmployeeDetailPage.js';
import { SchedulesPage } from './routes/SchedulesPage.js';
import { UsersPage } from './routes/UsersPage.js';
import { AccessGroupsPage } from './routes/AccessGroupsPage.js';
import { AccessGroupDetailPage } from './routes/AccessGroupDetailPage.js';
import { BuildingsPage } from './routes/BuildingsPage.js';
import { UnitsPage } from './routes/UnitsPage.js';
import { HouseholdsPage } from './routes/HouseholdsPage.js';
import { ResidentsPage } from './routes/ResidentsPage.js';
import { AmenitiesPage } from './routes/AmenitiesPage.js';
import { GuestPassesPage } from './routes/GuestPassesPage.js';
import { CallboxDirectoryPage } from './routes/CallboxDirectoryPage.js';
import { AdminUsersPage } from './routes/AdminUsersPage.js';
import { AdminWebhooksPage } from './routes/AdminWebhooksPage.js';
import { OutboxPage } from './routes/OutboxPage.js';
import { DefaultTenantSettingsPage } from './routes/DefaultTenantSettingsPage.js';
import { CredentialEnrollmentPage } from './routes/CredentialEnrollmentPage.js';
import { GlobalLockdownPage } from './routes/GlobalLockdownPage.js';
import { ResidentHomePage } from './routes/ResidentHomePage.js';
import { BulkMoveInPage } from './routes/BulkMoveInPage.js';
import { MyCredentialsPage } from './routes/MyCredentialsPage.js';
import { MyEventsPage } from './routes/MyEventsPage.js';
import { PreLaunchAuditPage } from './routes/PreLaunchAuditPage.js';
// Tranche AH pass 1 — operational detail pages.
import { TenantDetailPage } from './routes/TenantDetailPage.js';
import { UserDetailPage } from './routes/UserDetailPage.js';
import { DoorDetailPage } from './routes/DoorDetailPage.js';
import { ScheduleDetailPage } from './routes/ScheduleDetailPage.js';
import { OutboxJobDetailPage } from './routes/OutboxJobDetailPage.js';
import { EventDetailPage } from './routes/EventDetailPage.js';
import { AuditEntryDetailPage } from './routes/AuditEntryDetailPage.js';
// Tranche T1 — setup & branding sweep.
import { SetupBrandingPage } from './routes/SetupBrandingPage.js';
import { AdminSettingsPage } from './routes/AdminSettingsPage.js';
import { UbiquitiApiSetupWizardPage } from './routes/UbiquitiApiSetupWizardPage.js';
import { BrandingProvider } from './lib/branding.js';
import { ThemeProvider } from './lib/theme.js';
// Tranche T2 — intercom live call & ops.
import { IntercomManagementPage } from './routes/IntercomManagementPage.js';
import { IntercomOperationsPage } from './routes/IntercomOperationsPage.js';
import { IntercomMonitoringPage } from './routes/IntercomMonitoringPage.js';
import { IncomingCallPage } from './routes/IncomingCallPage.js';
import { ActiveIntercomCallPage } from './routes/ActiveIntercomCallPage.js';
// Tranche T3 — vendor & mobile access.
import { VendorAccessPage } from './routes/VendorAccessPage.js';
import { TenantMobileHardwareControlPage } from './routes/TenantMobileHardwareControlPage.js';
// Tranche T4 — variants & amenity reservations.
import { AmenityReservationsPage } from './routes/AmenityReservationsPage.js';
import { EmployeeWorkplaceHomePage } from './routes/EmployeeWorkplaceHomePage.js';
// PRs #54/#55 — credential policy admin (preserved across merge).
import { AdminCredentialsPage } from './routes/AdminCredentialsPage.js';
import { AdminCredentialInventoryPage } from './routes/AdminCredentialInventoryPage.js';
import { JourneysPage } from './routes/JourneysPage.js';
import { ShellLayout } from './layout/ShellLayout.js';
import { fetchSetupStatus, type SetupStatus } from './lib/setupStatus.js';

export function App() {
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null);
  const [setupUnavailable, setSetupUnavailable] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setSetupUnavailable(false);
    fetchSetupStatus()
      .then((s) => {
        if (!cancelled) setSetupStatus(s);
      })
      // A failed request used to fall back to `completed: false`, which sent
      // the operator of a live, configured install into the first-run setup
      // wizard — asking for a setup token, a site name, and a property type
      // warning that "switching later isn't supported".
      //
      // Observed on the Elkridge canary during a deploy: the API takes ~24s to
      // start listening, and any page load in that window presented the
      // wizard. An outage must not masquerade as an unconfigured install.
      .catch(() => {
        if (!cancelled) setSetupUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [retryTick]);

  // GAP-A1 (audit 2026-05-21): document.title was hardcoded to
  // "NG-UNIFI · Northgate Park Access" in index.html. Drive it from
  // setupStatus.site_name so the browser tab matches the install.
  useEffect(() => {
    if (!setupStatus) return;
    const name = setupStatus.site_name?.trim() || 'Access Portal';
    document.title = `${name} · NG-UNIFI`;
  }, [setupStatus]);

  if (setupUnavailable) {
    return <SetupUnavailable onRetry={() => setRetryTick((t) => t + 1)} />;
  }
  if (!setupStatus) return <FullScreenSpinner />;

  return (
    // Outside AuthProvider: /sign-in, /setup and the invite pages are themed
    // too, and a theme should not depend on being signed in.
    <ThemeProvider>
    <AuthProvider>
      <TenantScopeProvider siteMode={setupStatus.site_mode}>
        <SiteModeProvider mode={setupStatus.site_mode}>
          <BrandingProvider>
            <AppRoutes setupStatus={setupStatus} />
          </BrandingProvider>
        </SiteModeProvider>
      </TenantScopeProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

function AppRoutes({ setupStatus }: { setupStatus: SetupStatus }) {
  return (
    <>
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/sign-in" element={<SignInPage siteName={setupStatus.site_name} />} />
        <Route
          path="/forgot-password"
          element={<ForgotPasswordPage siteName={setupStatus.site_name} />}
        />
        <Route
          path="/accept-invite"
          element={<AcceptInvitePage siteName={setupStatus.site_name} />}
        />
        <Route path="/reset" element={<AcceptInvitePage siteName={setupStatus.site_name} />} />
        {/* /reset-password is the link sent in password-reset emails
            (see auth.ts /auth/forgot). Kept alongside /reset and
            /accept-invite for compat with any externally-cached URLs. */}
        <Route
          path="/reset-password"
          element={<AcceptInvitePage siteName={setupStatus.site_name} />}
        />
        <Route element={<RequireSetup completed={setupStatus.completed} />}>
          <Route element={<RequireAuth />}>
            <Route
              element={
                <ShellLayout siteMode={setupStatus.site_mode} siteName={setupStatus.site_name} />
              }
            >
              <Route path="/" element={<HomeForRole />} />
              <Route path="/account/security" element={<AccountSecurityPage />} />

              {/* "My own access" — every authenticated user has credentials
                  and an event history, and /api/v1/me/* is caller-scoped, so
                  these two are not resident concepts. They were behind a
                  resident-only guard while the only links to them lived on
                  /me/workplace, which residents cannot open and everyone else
                  was bounced away from (UX-001). EmployeeWorkplaceHomePage
                  already calls the same endpoints successfully for operators,
                  which is the evidence that the guard, not the page, was
                  wrong. */}
              <Route path="/me/credentials" element={<MyCredentialsPage />} />
              <Route path="/me/events" element={<MyEventsPage />} />

              {/* Resident-only self-service. A household, its guest passes and
                  its amenity bookings are genuinely resident concepts, so
                  these keep the tighter guard. */}
              <Route element={<RequireRole roles={['resident']} />}>
                <Route path="/me/home" element={<ResidentHomePage />} />
                {/* T4 — resident-scope amenity reservations. */}
                <Route
                  path="/me/amenities/reservations"
                  element={<AmenityReservationsPage scope="resident" />}
                />
              </Route>

              {/* Multi-family-only CRUD surfaces.

                  UX-002: these are operator surfaces but carried no role
                  guard, so a resident who followed the shell's unfiltered
                  menu landed on them and watched every request 403. Nav
                  filtering is not authorization — the guard is what stops a
                  deep link. */}
              <Route element={<RequireMode mode="multi_family" siteMode={setupStatus.site_mode} />}>
                <Route
                  element={
                    <RequireRole roles={['master_admin', 'tenant_admin', 'tenant_manager']} />
                  }
                >
                  <Route path="/residents/bulk-move-in" element={<BulkMoveInPage />} />
                  <Route path="/buildings" element={<BuildingsPage />} />
                  <Route path="/units" element={<UnitsPage />} />
                  <Route path="/households" element={<HouseholdsPage />} />
                  <Route path="/residents" element={<ResidentsPage />} />
                  <Route path="/amenities" element={<AmenitiesPage />} />
                  <Route path="/guest-passes" element={<GuestPassesPage />} />
                </Route>
                {/* T2 — intercom live call + ops (multi-family only since
                    callbox itself is multi-family-only). Operator surfaces,
                    same UX-002 reasoning as the block above. */}
                <Route
                  element={
                    <RequireRole roles={['master_admin', 'tenant_admin', 'tenant_manager']} />
                  }
                >
                  <Route path="/intercom" element={<IntercomManagementPage />} />
                  <Route path="/intercom/ops" element={<IntercomOperationsPage />} />
                  <Route path="/intercom/monitor" element={<IntercomMonitoringPage />} />
                  <Route path="/intercom/incoming/:id" element={<IncomingCallPage />} />
                  <Route path="/intercom/active/:id" element={<ActiveIntercomCallPage />} />
                  {/* T4 — operator-scope amenity reservations (multi-family). */}
                  <Route
                    path="/admin/amenities/reservations"
                    element={<AmenityReservationsPage scope="operator" />}
                  />
                </Route>
              </Route>

              {/* Shared operator surfaces (master + tenant_admin /
                  tenant_manager / tenant_viewer).

                  UX-002: "shared" used to include residents, because the
                  routes had no role guard and the shell offered them the
                  links. A resident is not an operator. */}
              <Route element={<RequireRole roles={OPERATOR_ROLES} />}>
                <Route path="/activity" element={<ActivityLogPage />} />
                <Route path="/hardware" element={<HardwareHealthPage />} />
                <Route path="/employees" element={<EmployeesPage />} />
                <Route path="/employees/:id" element={<EmployeeDetailPage />} />
                <Route path="/journeys" element={<JourneysPage />} />
                <Route path="/schedules" element={<SchedulesPage />} />
                <Route path="/schedules/:id" element={<ScheduleDetailPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/users/:id" element={<UserDetailPage scope="tenant" />} />
                <Route path="/access-groups" element={<AccessGroupsPage />} />
                <Route path="/hardware/doors/:id" element={<DoorDetailPage />} />
                <Route path="/activity/:id" element={<AuditEntryDetailPage />} />
                <Route path="/events/:id" element={<EventDetailPage />} />
                <Route path="/callbox" element={<CallboxDirectoryPage />} />
              </Route>
              {/* T3 — vendor portal (master + tenant_admin) and
                  tenant mobile hardware control (master + tenant_admin
                  + tenant_manager). Each gets a route-level RequireRole
                  guard so a tenant_manager / tenant_viewer / resident
                  deep-linking to /admin/vendors gets redirected by the
                  router instead of rendering a permission-error page
                  and exposing the URL surface. Pages keep their internal
                  role checks as defense-in-depth. */}
              <Route element={<RequireRole roles={['master_admin', 'tenant_admin']} />}>
                <Route path="/admin/vendors" element={<VendorAccessPage />} />
              </Route>
              <Route
                element={<RequireRole roles={['master_admin', 'tenant_admin', 'tenant_manager']} />}
              >
                <Route path="/me/hardware" element={<TenantMobileHardwareControlPage />} />
                {/* UX-033: /mobile/operator, /employees/mobile-activations and
                    /admin/mobile-activations all rendered this same component
                    with identical props and were linked from nowhere.
                    Confirmed with the product owner that enrollment is
                    entirely in-app, so no external entry point held those
                    URLs. /credentials/enroll is the single surface. */}
                <Route path="/credentials/enroll" element={<CredentialEnrollmentPage />} />
              </Route>
              {/* T4 — commercial employee workplace home (any authed
                  employee in a non-multi-family mode). */}
              <Route path="/me/workplace" element={<EmployeeWorkplaceHomePage />} />

              {/* Master-admin-only surfaces. Backend capability checks
                  protect the API; this UI guard prevents tenant users
                  from deep-linking into a 403-rendered page.
                  (Audit GAP-T1 / GAP-T2 follow-up — PR #53 review.) */}
              <Route element={<RequireMaster />}>
                <Route path="/access-groups/:id" element={<AccessGroupDetailPage />} />
                <Route path="/admin/access-zones" element={<AdminAccessZonesPage />} />
                <Route path="/hardware/claim" element={<HardwareClaimPage />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/users/:id" element={<UserDetailPage scope="admin" />} />
                <Route path="/admin/webhooks" element={<AdminWebhooksPage />} />
                <Route path="/admin/lockdown" element={<GlobalLockdownPage />} />
                <Route path="/admin/pre-launch" element={<PreLaunchAuditPage />} />
                {/* Four surfaces removed 2026-09-04 as duplicates — see
                    docs/ux-review/NAVIGATION_AUDIT.md. /admin/multi-tenant was
                    a five-line <Navigate to="/">; /admin/property counted what
                    the dashboard already counts; /admin/integration-review's
                    own comment said it reused PreLaunchAuditPage's signals;
                    /admin/security/mobile called the same two lockdown
                    endpoints as /admin/lockdown. Old bookmarks land on the
                    catch-all, which sends them to the dashboard. */}
                <Route path="/admin/branding" element={<SetupBrandingPage />} />
                <Route path="/admin/unifi-setup-wizard" element={<UbiquitiApiSetupWizardPage />} />
                <Route path="/admin/credentials" element={<AdminCredentialsPage />} />
                <Route
                  path="/admin/credentials/inventory"
                  element={<AdminCredentialInventoryPage />}
                />
                {/* The hub the dashboard's failed-sync alert was already
                    linking to before it existed (UX-004). */}
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
                <Route
                  path="/admin/settings/default-tenant"
                  element={<DefaultTenantSettingsPage />}
                />
                <Route path="/outbox" element={<OutboxPage />} />
                <Route path="/outbox/:id" element={<OutboxJobDetailPage />} />
                {/* /tenants is BOTH master-only and commercial-mode-only.
                    The RequireMode guard is the inner one because mode
                    is the more restrictive concept — non-commercial
                    masters get redirected to /. */}
                <Route
                  element={
                    <RequireMode mode="commercial_multi_tenant" siteMode={setupStatus.site_mode} />
                  }
                >
                  <Route path="/tenants" element={<TenantsPage />} />
                  <Route path="/tenants/:id" element={<TenantDetailPage />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function RequireSetup({ completed }: { completed: boolean }) {
  const location = useLocation();
  if (!completed && location.pathname !== '/setup') {
    return <Navigate to="/setup" replace />;
  }
  return <Outlet />;
}

function RequireAuth() {
  const { state } = useAuth();
  if (state.status === 'loading') return <FullScreenSpinner />;
  if (state.status !== 'authed') return <Navigate to="/sign-in" replace />;
  return <Outlet />;
}

// Route-level guard for master-only admin surfaces. Nav filtering alone
// is not enough because direct URL entry/bookmarks can bypass it.
function RequireMaster() {
  const { state } = useAuth();
  if (state.status === 'loading') return <FullScreenSpinner />;
  if (state.status !== 'authed') return <Navigate to="/sign-in" replace />;
  if (state.user.role !== 'master_admin') return <Navigate to="/" replace />;
  return <Outlet />;
}

// Generic role-restricted route. Reviewer follow-up to PR #53: block
// non-residents from /me/* and similarly let any future role-scoped
// surface use one consistent guard.
type AuthRole = 'master_admin' | 'tenant_admin' | 'tenant_manager' | 'tenant_viewer' | 'resident';
/** Every authenticated role except `resident`. */
const OPERATOR_ROLES: AuthRole[] = [
  'master_admin',
  'tenant_admin',
  'tenant_manager',
  'tenant_viewer',
];
function RequireRole({ roles }: { roles: AuthRole[] }) {
  const { state } = useAuth();
  if (state.status === 'loading') return <FullScreenSpinner />;
  if (state.status !== 'authed') return <Navigate to="/sign-in" replace />;
  if (!roles.includes(state.user.role as AuthRole)) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

// GAP-T1 / GAP-T2 (audit 2026-05-21): some routes only make sense in one
// site_mode (e.g. /tenants is master-only in commercial mode; the resident
// CRUD pages only exist in multi_family). Nav already hides the links;
// this guard plugs the direct-URL hole by redirecting to the dashboard
// instead of letting an operator land on a mode-foreign page.
function RequireMode({
  mode,
  siteMode,
}: {
  mode: SetupStatus['site_mode'];
  siteMode: SetupStatus['site_mode'];
}) {
  if (mode !== siteMode) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Tranche CA: residents land on /me/home; everyone else (master + tenant
// admins) lands on the admin dashboard. Without this, residents sign in
// and see admin nav items that all 403 server-side.
function HomeForRole() {
  const { state } = useAuth();
  if (state.status === 'authed' && state.user.role === 'resident') {
    return <Navigate to="/me/home" replace />;
  }
  return <AdminDashboardPage />;
}

/**
 * Shown when `/setup/status` could not be reached — distinct from an install
 * that genuinely has not been set up. Says what is wrong, does not offer to
 * reconfigure anything, and lets the operator retry without a full reload.
 */
function SetupUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-xl border border-outline-variant bg-surface-container-lowest p-6 text-center">
        <h1 className="font-headline-sm text-headline-sm text-on-surface">
          Can’t reach the portal
        </h1>
        <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
          The server did not answer. This is usually brief — it happens while the
          service restarts. Your site’s configuration has not changed.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center rounded-md bg-primary px-4 py-2 font-label-md text-label-md text-on-primary hover:bg-opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function FullScreenSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-surface">
      <div
        aria-label="Loading"
        className="h-8 w-8 animate-spin rounded-full border-2 border-on-surface-variant border-t-primary"
      />
    </div>
  );
}
